/**
 *  @class
 *  @function DesignersList
 *  Indice designer A-Z: barra alfabetica sticky con scroll animato,
 *  ricerca live e rivelazione a cascata dei gruppi allo scroll.
 *  Usa GSAP + ScrollTrigger (gia' caricati dal tema quando le animazioni
 *  sono attive) e rispetta prefers-reduced-motion.
 */

if (!customElements.get('designers-list')) {
  class DesignersList extends HTMLElement {
    constructor() {
      super();

      this.groups = Array.from(this.querySelectorAll('.designers-list__group'));
      this.items = Array.from(this.querySelectorAll('.designers-list__item'));
      this.nav = this.querySelector('[data-designers-nav]');
      this.navItems = Array.from(this.querySelectorAll('.designers-list__nav-item[data-letter]'));
      this.searchField = this.querySelector('[data-designers-search]');
      this.clearButton = this.querySelector('[data-designers-clear]');
      this.emptyMessage = this.querySelector('[data-designers-empty]');
      this.countElement = this.querySelector('[data-designers-count]');
      this.countTemplate = this.countElement
        ? this.countElement.dataset.countTemplate || ''
        : '';
      this.toolbar = this.querySelector('.designers-list__toolbar');

      this.animations_enabled =
        document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.revealTimelines = [];
      this.revealReleased = false;
      this.searchTimer = null;
      this.scrollSpyFrame = null;
      this.activeLetter = null;

      // Nome normalizzato (minuscolo, senza accenti) per la ricerca.
      this.items.forEach((item) => {
        item.searchName = DesignersList.normalize(item.dataset.name || item.textContent);
      });
    }

    static normalize(value) {
      return (value || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    }

    connectedCallback() {
      this.onNavClick = this.onNavClick.bind(this);
      this.onSearchInput = this.onSearchInput.bind(this);
      this.onScrollSpy = this.onScrollSpy.bind(this);

      this.navItems.forEach((item) => item.addEventListener('click', this.onNavClick));

      if (this.searchField) {
        this.searchField.addEventListener('input', this.onSearchInput);
        this.searchField.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.clearSearch();
        });
      }
      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => this.clearSearch());
      }

      window.addEventListener('scroll', this.onScrollSpy, { passive: true });
      this.onScrollSpy();

      if (this.animations_enabled) {
        this.prepareAnimations();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollSpy);
      clearTimeout(this.searchTimer);
      if (this.scrollSpyFrame) cancelAnimationFrame(this.scrollSpyFrame);
      if (this.mm) this.mm.revert();
    }

    /* Animazioni ------------------------------------------------------- */

    prepareAnimations() {
      const section = this;

      this.mm = gsap.matchMedia();
      this.mm.add(
        {
          reduceMotion: '(prefers-reduced-motion: reduce)',
          isMobile: '(max-width: 767px)',
        },
        (context) => {
          const { reduceMotion, isMobile } = context.conditions;

          // Utenti con riduzione del movimento: contenuto gia' visibile.
          if (reduceMotion) {
            gsap.set(section.groups, { opacity: 1, y: 0 });
            gsap.set(section.items, { opacity: 1, y: 0 });
            return;
          }

          section.revealTimelines = section.groups.map((group) => {
            const letter = group.querySelector('.designers-list__group-letter');
            const items = group.querySelectorAll('.designers-list__item');

            const tl = gsap.timeline({
              scrollTrigger: {
                trigger: group,
                start: 'top 85%',
              },
            });

            tl.from(letter, {
              opacity: 0,
              y: isMobile ? 16 : 24,
              duration: 0.5,
              clearProps: 'transform',
            }).from(
              items,
              {
                opacity: 0,
                y: isMobile ? 12 : 20,
                duration: isMobile ? 0.45 : 0.6,
                stagger: isMobile ? 0.03 : 0.045,
                clearProps: 'transform',
              },
              '<0.15'
            );

            return tl;
          });

          // Cleanup quando la condizione smette di essere valida.
          return () => {
            section.revealTimelines.forEach((tl) => tl.kill());
            section.revealTimelines = [];
          };
        }
      );
    }

    /**
     * Durante la ricerca i gruppi non ancora rivelati resterebbero invisibili:
     * si chiudono i timeline sullo stato finale e si liberano gli stili inline.
     */
    releaseRevealAnimations() {
      if (!this.animations_enabled || this.revealReleased) return;

      this.revealTimelines.forEach((tl) => {
        if (tl.scrollTrigger) tl.scrollTrigger.kill();
        tl.progress(1).kill();
      });
      this.revealTimelines = [];
      gsap.set(this.groups.concat(this.items), { clearProps: 'all' });
      this.revealReleased = true;
    }

    /* Ricerca ---------------------------------------------------------- */

    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.filter(this.searchField.value), 150);
    }

    clearSearch() {
      if (!this.searchField) return;
      this.searchField.value = '';
      this.filter('');
      this.searchField.focus();
    }

    filter(rawQuery) {
      const query = DesignersList.normalize(rawQuery);
      const revealed = [];
      let visibleCount = 0;

      if (query) this.releaseRevealAnimations();

      this.items.forEach((item) => {
        const matches = !query || item.searchName.indexOf(query) !== -1;
        const wasHidden = item.classList.contains('is-hidden');

        item.classList.toggle('is-hidden', !matches);
        if (matches) {
          visibleCount += 1;
          if (wasHidden) revealed.push(item);
        }
      });

      // Un gruppo senza risultati sparisce, insieme alla sua lettera in barra.
      const activeLetters = [];
      this.groups.forEach((group) => {
        const hasVisible = !!group.querySelector('.designers-list__item:not(.is-hidden)');
        group.classList.toggle('is-hidden', !hasVisible);
        if (hasVisible) activeLetters.push(group.dataset.letter);
      });

      this.navItems.forEach((item) => {
        item.classList.toggle('is-muted', activeLetters.indexOf(item.dataset.letter) === -1);
      });

      if (this.clearButton) this.clearButton.hidden = !query;
      if (this.emptyMessage) this.emptyMessage.hidden = visibleCount > 0;
      this.updateCount(visibleCount);

      if (this.animations_enabled && !this.reduceMotion && revealed.length) {
        gsap.fromTo(
          revealed,
          { opacity: 0, y: 8 },
          {
            opacity: 1,
            y: 0,
            duration: 0.3,
            stagger: 0.02,
            clearProps: 'transform',
          }
        );
      }

      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
      this.activeLetter = null;
      this.onScrollSpy();
    }

    // Il totale segue i risultati filtrati; a ricerca vuota torna al totale
    // reso da Liquid, perche' visibleCount coincide con tutte le voci.
    updateCount(visibleCount) {
      if (!this.countElement || !this.countTemplate) return;
      this.countElement.textContent = this.countTemplate.split('{count}').join(visibleCount);
    }

    /* Navigazione alfabetica ------------------------------------------- */

    onNavClick(event) {
      const letter = event.currentTarget.dataset.letter;
      const group = this.groups.find(
        (item) => item.dataset.letter === letter && !item.classList.contains('is-hidden')
      );
      if (!group) return;

      event.preventDefault();
      this.scrollToGroup(group);
    }

    get scrollOffset() {
      const headerHeight =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
          10
        ) || 0;
      const stickyToolbar =
        this.toolbar && this.toolbar.classList.contains('designers-list__toolbar--sticky')
          ? this.toolbar.offsetHeight
          : 0;

      return headerHeight + stickyToolbar + 20;
    }

    scrollToGroup(group) {
      const top =
        group.getBoundingClientRect().top + document.documentElement.scrollTop - this.scrollOffset;

      window.scrollTo({
        top: top,
        left: 0,
        behavior: this.reduceMotion ? 'auto' : 'smooth',
      });
    }

    onScrollSpy() {
      if (this.scrollSpyFrame) return;

      this.scrollSpyFrame = requestAnimationFrame(() => {
        this.scrollSpyFrame = null;

        const threshold = this.scrollOffset + 10;
        let current = null;

        this.groups.forEach((group) => {
          if (group.classList.contains('is-hidden')) return;
          if (group.getBoundingClientRect().top <= threshold) current = group.dataset.letter;
        });

        if (current === this.activeLetter) return;
        this.activeLetter = current;

        this.navItems.forEach((item) => {
          const isActive = item.dataset.letter === current;
          item.classList.toggle('is-active', isActive);
          if (isActive) {
            item.setAttribute('aria-current', 'true');
          } else {
            item.removeAttribute('aria-current');
          }
        });

        this.scrollActiveNavIntoView();
      });
    }

    // Su mobile la barra scorre in orizzontale: la lettera attiva resta a vista.
    scrollActiveNavIntoView() {
      if (!this.nav) return;

      const active = this.nav.querySelector('.designers-list__nav-item.is-active');
      if (!active || this.nav.scrollWidth <= this.nav.clientWidth) return;

      const navRect = this.nav.getBoundingClientRect();
      const itemRect = active.getBoundingClientRect();

      if (itemRect.left < navRect.left || itemRect.right > navRect.right) {
        this.nav.scrollTo({
          left:
            this.nav.scrollLeft +
            (itemRect.left - navRect.left) -
            (navRect.width - itemRect.width) / 2,
          behavior: this.reduceMotion ? 'auto' : 'smooth',
        });
      }
    }
  }

  customElements.define('designers-list', DesignersList);
}
